export type ProjectHandlerKind = "log" | "query" | "draft";

export type ActiveProjectConfig = {
  projectTitle: string;
  intentNames: string[];
  intents: Record<string, string>;
  entryType?: string;
  entryFields?: string;
  queryRangeDefault?: string;
  confirmationTemplate?: string;
};

const HANDLER_BY_INDEX: ProjectHandlerKind[] = ["log", "query", "draft"];

function parseIntentsBlock(lines: string[]): {
  intentNames: string[];
  intents: Record<string, string>;
} {
  const intentNames: string[] = [];
  const intents: Record<string, string> = {};

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const bulletMatch = trimmed.match(/^[-*]\s*(\w+)\s*:\s*(.+)$/);
    const plainMatch = trimmed.match(/^(\w+)\s*:\s*(.+)$/);
    const match = bulletMatch ?? plainMatch;
    if (!match) continue;

    const name = match[1].trim();
    const description = match[2].trim();
    if (!name || !description) continue;

    intentNames.push(name);
    intents[name] = description;
  }

  return { intentNames, intents };
}

/** Defensive parser — never throws; returns null on missing/malformed input. */
export function parseActiveProject(content: string): ActiveProjectConfig | null {
  const text = content.trim();
  if (!text) return null;

  const lines = text.split("\n");
  let projectTitle = "";
  const intentLines: string[] = [];
  let entryType: string | undefined;
  let entryFields: string | undefined;
  let queryRangeDefault: string | undefined;
  let confirmationTemplate: string | undefined;
  let inIntentsBlock = false;

  for (const line of lines) {
    const trimmed = line.trim();

    const activeProjectMatch = trimmed.match(/^Active project:\s*(.+)$/i);
    if (activeProjectMatch) {
      projectTitle = activeProjectMatch[1].trim();
      inIntentsBlock = false;
      continue;
    }

    if (/^Intents:\s*$/i.test(trimmed)) {
      inIntentsBlock = true;
      continue;
    }

    const entryTypeMatch = trimmed.match(/^Entry type:\s*(.+)$/i);
    if (entryTypeMatch) {
      entryType = entryTypeMatch[1].trim();
      inIntentsBlock = false;
      continue;
    }

    const entryFieldsMatch = trimmed.match(/^Entry fields:\s*(.+)$/i);
    if (entryFieldsMatch) {
      entryFields = entryFieldsMatch[1].trim();
      inIntentsBlock = false;
      continue;
    }

    const queryDefaultMatch = trimmed.match(/^Query range default:\s*(.+)$/i);
    if (queryDefaultMatch) {
      queryRangeDefault = queryDefaultMatch[1].trim();
      inIntentsBlock = false;
      continue;
    }

    const confirmationMatch = trimmed.match(/^Confirmation:\s*(.+)$/i);
    if (confirmationMatch) {
      confirmationTemplate = confirmationMatch[1].trim();
      inIntentsBlock = false;
      continue;
    }

    if (inIntentsBlock && trimmed) {
      intentLines.push(trimmed);
    }
  }

  const { intentNames, intents } = parseIntentsBlock(intentLines);
  if (intentNames.length === 0 && !entryType) {
    return null;
  }

  return {
    projectTitle: projectTitle || text.split("\n")[0]?.trim() || "Active project",
    intentNames,
    intents,
    entryType,
    entryFields,
    queryRangeDefault,
    confirmationTemplate,
  };
}

export function getDynamicIntentNames(
  config: ActiveProjectConfig | null,
): string[] {
  if (!config) return [];
  return [...config.intentNames];
}

export function resolveProjectHandler(
  intent: string,
  config: ActiveProjectConfig | null,
): ProjectHandlerKind | null {
  if (!config) return null;

  const index = config.intentNames.indexOf(intent);
  if (index < 0 || index >= HANDLER_BY_INDEX.length) return null;

  return HANDLER_BY_INDEX[index] ?? null;
}
